import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Gift,
  HeartPulse,
  Sparkles,
  Bell,
  ChevronLeft,
  ChevronRight,
  X,
  MapPin,
  Calendar,
  Building2
} from 'lucide-react';

const EVENT_TYPE_CONFIG = {
  promotion: { icon: Gift, color: 'bg-orange-500', lightBg: 'bg-orange-100', text: 'text-orange-700' },
  health_campaign: { icon: HeartPulse, color: 'bg-green-500', lightBg: 'bg-green-100', text: 'text-green-700' },
  new_product: { icon: Sparkles, color: 'bg-blue-500', lightBg: 'bg-blue-100', text: 'text-blue-700' },
  announcement: { icon: Bell, color: 'bg-purple-500', lightBg: 'bg-purple-100', text: 'text-purple-700' }
};

const EventBanner = () => {
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadFeaturedEvents = useCallback(async () => {
    try {
      const response = await api.get('/events/featured?limit=5');
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error loading featured events:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeaturedEvents();
  }, [loadFeaturedEvents]);

  // Auto-rotate events every 5 seconds
  useEffect(() => {
    if (events.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % events.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [events.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + events.length) % events.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % events.length);
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('eventBannerDismissed', 'true');
  };

  // Check if already dismissed this session
  useEffect(() => {
    if (sessionStorage.getItem('eventBannerDismissed') === 'true') {
      setDismissed(true);
    }
  }, []);

  if (loading || dismissed || events.length === 0) {
    return null;
  }

  const currentEvent = events[currentIndex];
  const typeConfig = EVENT_TYPE_CONFIG[currentEvent.event_type] || EVENT_TYPE_CONFIG.announcement;
  const TypeIcon = typeConfig.icon;

  return (
    <div 
      className="relative bg-gradient-to-r from-[#1A237E] via-[#303F9F] to-[#1A237E] text-white"
      data-testid="event-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Navigation arrows - Desktop */}
          {events.length > 1 && (
            <button
              onClick={handlePrev}
              className="hidden sm:flex w-8 h-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Événement précédent"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          {/* Event Content */}
          <div className="flex-1 flex items-center justify-center gap-3 sm:gap-4 min-w-0">
            <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${typeConfig.color} flex items-center justify-center`}>
              <TypeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${typeConfig.lightBg} ${typeConfig.text} text-xs`}>
                  {currentEvent.event_type === 'promotion' && 'Promotion'}
                  {currentEvent.event_type === 'health_campaign' && 'Campagne santé'}
                  {currentEvent.event_type === 'new_product' && 'Nouveau'}
                  {currentEvent.event_type === 'announcement' && 'Annonce'}
                </Badge>
                {currentEvent.facility?.name && (
                  <span className="text-xs text-white/70 flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {currentEvent.facility.name}
                  </span>
                )}
              </div>
              
              <h3 className="font-semibold text-sm sm:text-base truncate mt-1">
                {currentEvent.title}
              </h3>
              
              <p className="text-xs sm:text-sm text-white/80 truncate hidden sm:block">
                {currentEvent.description}
              </p>
              
              <div className="flex items-center gap-3 mt-1 text-xs text-white/60">
                {currentEvent.facility_city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {currentEvent.facility_city}
                  </span>
                )}
                {currentEvent.start_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(currentEvent.start_date).toLocaleDateString('fr-FR')}
                  </span>
                )}
                {currentEvent.promo_code && (
                  <span className="bg-white/20 px-2 py-0.5 rounded font-mono">
                    {currentEvent.promo_code}
                  </span>
                )}
              </div>
            </div>
            
            {/* CTA Button */}
            <Link 
              to={currentEvent.facility_id ? `/facilities/${currentEvent.facility_id}` : '/facilities'}
              className="flex-shrink-0"
            >
              <Button 
                size="sm"
                className="bg-white text-[#1A237E] hover:bg-white/90 font-semibold rounded-full px-4"
              >
                Voir
              </Button>
            </Link>
          </div>

          {/* Navigation arrows - Desktop */}
          {events.length > 1 && (
            <button
              onClick={handleNext}
              className="hidden sm:flex w-8 h-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Événement suivant"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
            aria-label="Fermer la bannière"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Dots indicator */}
        {events.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-2 sm:hidden">
            {events.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-white' : 'bg-white/30'
                }`}
                aria-label={`Aller à l'événement ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventBanner;
